import { useEffect, useId, useRef } from 'react';
import { Button, Input } from '../ui';
import { CountrySelect } from './CountrySelect';
import { ResendCountdown } from './ResendCountdown';
import { usePhoneVerification, type CodeSentHandler } from '../../../lib/use-phone-verification';
import type { CodeWaitCarry } from '../../../lib/code-wait-carry';
import { useTranslation } from '../../../lib/i18n';
import styles from './PhoneScreen.module.css';

// The number fields. The verification dialog is the only surface that asks for a number — there is no
// full-screen version any more, and `/onboarding/phone` forwards to `/welcome` (see onboarding-gate's
// RETIRED_ONBOARDING). `onCodeSent` is how the dialog moves to its own code step.
export function PhoneStepBody({
  onCodeSent,
  codeWait,
}: { onCodeSent?: CodeSentHandler; codeWait?: CodeWaitCarry } = {}) {
  const { t } = useTranslation();
  const phone = usePhoneVerification({ onCodeSent, codeWait });
  const numberRef = useRef<HTMLInputElement | null>(null);
  // Per instance: the dialog and the post-purchase takeover can both be mounted.
  const countdownId = useId();
  // The code step's wait, carried back here because the number in the field is the one the code went
  // to. It greys both buttons through `aria-disabled` rather than `disabled`, as the code step's resend
  // buttons do: a real `disabled` drops the button out of the tab order, so a keyboard or screen-reader
  // user would pass it with the countdown never read. The press is refused in `send`, against the deadline.
  const carried = phone.carriedIn > 0;
  // Shown, and pointed at, only while no 429 message is up: that message counts the seconds itself, and
  // two countdowns read as two waits. One value for both, so `aria-describedby` never names a missing id.
  const countdownShown = carried && phone.blockedIn === 0;
  const carriedProps = {
    ...(carried ? { 'aria-disabled': true as const } : {}),
    ...(countdownShown ? { 'aria-describedby': countdownId } : {}),
  };

  useEffect(() => {
    // React's `autoFocus` did not survive this screen's mount, and the number field is
    // the only thing to do here — so focus it explicitly.
    numberRef.current?.focus();
  }, []);

  // The AppsFlyer onboarding-start event no longer fires here: it is now latched once per
  // browser at the user's first chat send while onboarding is unfinished (see lib/use-chat.ts
  // + lib/appsflyer.ts), not on the phone/verify screen.
  function startAndSend(send: () => Promise<void>) {
    void send();
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        startAndSend(phone.sendSms);
      }}
      noValidate
    >
      <div className={styles.row}>
        <CountrySelect
          countries={phone.countries}
          value={phone.iso}
          onChange={(next) => {
            phone.logCountryPick(next);
            phone.setIso(next);
          }}
          onOpen={phone.logCountryListOpen}
          disabled={phone.submitting}
        />
        <Input
          ref={numberRef}
          className={styles.number}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={t('onboarding.phone.input.label')}
          aria-label={t('onboarding.phone.input.label')}
          value={phone.phone}
          onChange={(e) => phone.setPhone(e.target.value)}
          onBlur={phone.onBlur}
          status={phone.showInvalid ? 'error' : 'default'}
          statusMessage={phone.showInvalid ? t('onboarding.phone.error.invalidNumber') : undefined}
        />
      </div>

      {phone.error && (
        <p className={styles.error} role="alert">
          {phone.error}
        </p>
      )}

      {countdownShown ? (
        <ResendCountdown
          id={countdownId}
          className={styles.countdown}
          label={t('onboarding.code.symphony.resendIn', { seconds: phone.carriedIn })}
          left={phone.carriedLeft}
        />
      ) : null}

      {/* Both channels go dark together while either wait runs. The server throttles the CALLER, so the
          other channel is behind the same wall and a live button there only earns a second refusal. The
          error line or the countdown above states the reason for the grey. */}
      <div className={styles.actions}>
        <Button
          type="submit"
          size="large"
          fullWidth
          disabled={!phone.isValid || phone.blockedIn > 0}
          loading={phone.submitting}
          {...carriedProps}
        >
          {t('onboarding.phone.symphony.cta.sms')}
        </Button>
        <Button
          type="button"
          size="large"
          fullWidth
          variant="outlined"
          disabled={!phone.isValid || phone.submitting || phone.blockedIn > 0}
          onClick={() => startAndSend(phone.sendWhatsApp)}
          {...carriedProps}
        >
          {t('onboarding.phone.symphony.cta.whatsapp')}
        </Button>
      </div>
    </form>
  );
}
